import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type {
    DatepickerComparisonPeriodsAvailableType,
    DatepickerPeriodsAvailableType
} from '@/lib/periods';
import {
    LAST_7_DAYS,
    LAST_30_DAYS,
    PREVIOUS_MONTH,
    PREVIOUS_PERIOD,
    PREVIOUS_WEEK,
    PREVIOUS_YEAR,
    SAME_PERIOD_LAST_YEAR,
    THIS_YEAR
} from '@/lib/periods';

dayjs.extend(utc);
dayjs.extend(timezone);

type CalculateComparisonPeriodParams = {
    start: string;
    end: string;
    compareTo: DatepickerComparisonPeriodsAvailableType;
    period?: DatepickerPeriodsAvailableType;
    timezone?: string;
};

type ComparisonPeriodResult = {
    startComparison: string;
    endComparison: string;
};

class AnalyticsPeriodService {
    /**
     * Calculate the comparison period dates based on the current period and comparison type
     * @param params - Object containing start, end, compareTo, and optional timezone
     * @returns Object with startComparison and endComparison dates in ISO format
     */
    calculateComparisonPeriod({
        start,
        end,
        compareTo,
        period,
        timezone: _timezone = 'UTC'
    }: CalculateComparisonPeriodParams): ComparisonPeriodResult {
        // Parse dates in UTC mode (dates arrive déjà en UTC ISO format)
        // Note: timezone parameter kept for compatibility but not used as dates are already in UTC
        const startDate = dayjs.utc(start);
        const endDate = dayjs.utc(end);

        if (compareTo === SAME_PERIOD_LAST_YEAR) {
            return this.calculateSamePeriodLastYear(startDate, endDate);
        }

        if (compareTo === PREVIOUS_PERIOD) {
            return this.calculatePreviousPeriod(startDate, endDate, period);
        }

        throw new Error(`Unknown comparison type: ${compareTo}`);
    }

    /**
     * Calculate same period last year - subtract exactly 1 year
     */
    private calculateSamePeriodLastYear(
        startDate: dayjs.Dayjs,
        endDate: dayjs.Dayjs
    ): ComparisonPeriodResult {
        const startComparison = startDate.subtract(1, 'year');
        const endComparison = endDate.subtract(1, 'year');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * Calculate previous period - logic depends on predefined period type
     */
    private calculatePreviousPeriod(
        startDate: dayjs.Dayjs,
        endDate: dayjs.Dayjs,
        period?: DatepickerPeriodsAvailableType
    ): ComparisonPeriodResult {
        switch (period) {
            case THIS_YEAR:
            case PREVIOUS_YEAR:
                return this.calculatePreviousPeriodForYear(startDate, endDate);
            case PREVIOUS_WEEK:
                return this.calculatePreviousPeriodForWeek(startDate, endDate);
            case PREVIOUS_MONTH:
                return this.calculatePreviousPeriodForMonth(startDate, endDate);
            case LAST_7_DAYS:
                return this.calculatePreviousPeriodForLast7Days(startDate);
            case LAST_30_DAYS:
                return this.calculatePreviousPeriodForLast30Days(startDate);
            default:
                return this.calculatePreviousPeriodDefault(startDate, endDate);
        }
    }

    /**
     * THIS_YEAR / PREVIOUS_YEAR + PREVIOUS_PERIOD: whole previous year
     */
    private calculatePreviousPeriodForYear(
        startDate: dayjs.Dayjs,
        endDate: dayjs.Dayjs
    ): ComparisonPeriodResult {
        const startComparison = startDate.subtract(1, 'year').startOf('year');
        const endComparison = endDate.subtract(1, 'year').endOf('year');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * PREVIOUS_WEEK + PREVIOUS_PERIOD: week before
     */
    private calculatePreviousPeriodForWeek(
        startDate: dayjs.Dayjs,
        endDate: dayjs.Dayjs
    ): ComparisonPeriodResult {
        const startComparison = startDate.subtract(1, 'week').startOf('week');
        const endComparison = endDate.subtract(1, 'week').endOf('day');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * PREVIOUS_MONTH + PREVIOUS_PERIOD: whole previous month
     */
    private calculatePreviousPeriodForMonth(
        startDate: dayjs.Dayjs,
        endDate: dayjs.Dayjs
    ): ComparisonPeriodResult {
        const startComparison = startDate.subtract(1, 'month').startOf('month');
        const endComparison = endDate.subtract(1, 'month').endOf('month');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * LAST_7_DAYS + PREVIOUS_PERIOD: 7 days before current period
     */
    private calculatePreviousPeriodForLast7Days(startDate: dayjs.Dayjs): ComparisonPeriodResult {
        const endComparison = startDate.subtract(1, 'day').endOf('day');
        const startComparison = endComparison.subtract(6, 'day').startOf('day');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * LAST_30_DAYS + PREVIOUS_PERIOD: 30 days before current period
     */
    private calculatePreviousPeriodForLast30Days(startDate: dayjs.Dayjs): ComparisonPeriodResult {
        const endComparison = startDate.subtract(1, 'day').endOf('day');
        const startComparison = endComparison.subtract(29, 'day').startOf('day');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * CUSTOM or unknown period: exact duration, ending 1ms before start
     */
    private calculatePreviousPeriodDefault(
        startDate: dayjs.Dayjs,
        endDate: dayjs.Dayjs
    ): ComparisonPeriodResult {
        const durationMs = endDate.diff(startDate, 'millisecond');
        const endComparison = startDate.subtract(1, 'millisecond');
        const startComparison = endComparison.subtract(durationMs, 'millisecond');

        return {
            startComparison: startComparison.toISOString(),
            endComparison: endComparison.toISOString()
        };
    }

    /**
     * Calculate the evolution ratio between current and previous values
     * @param current - Current period value (can be null)
     * @param previous - Previous period value (can be null)
     * @returns Evolution ratio (e.g., 0.25 for 25% increase, -0.10 for 10% decrease) or null
     */
    calculateEvolution(current: number | null, previous: number | null): number | null {
        if (current === null || current === undefined || !previous || previous === 0) {
            return null;
        }

        // Calculate ratio change: (current - previous) / abs(previous)
        // Use abs() to handle negative previous values
        const evolution = (current - previous) / Math.abs(previous);

        // Round to 4 decimal places (same as backend)
        return Math.round(evolution * 10000) / 10000;
    }
}

const analyticsPeriodService = new AnalyticsPeriodService();

export { analyticsPeriodService as AnalyticsPeriodService };
